import React, { useState } from 'react';
import { MessageSquare, X } from 'lucide-react';
import { useFeedback, FeedbackType } from '../hooks/useFeedback';
import { trackEvent } from '../lib/analytics';

function FeedbackForm() {
  const [isOpen, setIsOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    type: '' as FeedbackType,
    role: '',
    thoughts: ''
  });

  const { submitFeedback, isSubmitting, error } = useFeedback();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await submitFeedback(formData);
    if (success) {
      trackEvent('feedback_submit', { 
        feedbackType: formData.type,
        role: formData.role 
      });
      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        setFormData({ type: '' as FeedbackType, role: '', thoughts: '' });
      }, 3000);
    }
  };

  // Prevent scrolling when the form is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* Feedback button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-4 right-4 md:bottom-6 md:right-6 bg-blue-600 text-white p-3 md:p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200 z-40 ${
          isOpen ? 'hidden' : 'block'
        }`}
        aria-label="Dela dina tankar"
      >
        <MessageSquare size={24} />
      </button>

      {/* Feedback form modal */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end md:items-center justify-center p-0 md:p-4 z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsOpen(false);
            }
          }}
        >
          <div className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full max-w-lg relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
              aria-label="Stäng"
            >
              <X size={24} />
            </button>

            {submitted ? (
              <div className="p-6 md:p-8 text-center">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Tack för dina tankar!</h3>
                <p className="text-gray-600">
                  Din anonyma feedback är värdefull för utvecklingen av Grantly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-6 md:p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Vad tycker du om idén?
                </h3>
                <p className="text-gray-500 text-sm mb-6">
                  Din feedback är helt anonym och hjälper oss förstå hur vi kan göra Grantly bättre.
                </p>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Hur ställer du dig till konceptet?
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as FeedbackType })}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                      required
                    >
                      <option value="">Välj ett alternativ</option>
                      <option value="interested">Skulle definitivt använda tjänsten</option>
                      <option value="not-interested">Inte relevant för mig</option>
                      <option value="suggestions">Intressant, men behöver anpassas</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Din roll
                    </label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      placeholder="T.ex. Startup-grundare, Projektledare, Forskare"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dina tankar om konceptet
                    </label>
                    <textarea
                      value={formData.thoughts}
                      onChange={(e) => setFormData({ ...formData, thoughts: e.target.value })}
                      placeholder="Skulle en AI-driven bidragsassistent vara värdefull för dig? Varför/varför inte? Vad skulle du vilja se i en sådan tjänst?"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent h-32 text-base"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 text-base disabled:opacity-50"
                  >
                    {isSubmitting ? 'Skickar...' : 'Skicka anonym feedback'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default FeedbackForm;